/*
 *  Author: DL
 *  Date: 2020-12-04 23:33:30
 *  Email: dunaliang@fengmap.com
 *  LastEditTime: 2020-12-15 17:08:49
 *  LastEditors: DL
 *  Description: 起终点选择
 *  Copyright: Copyright 2014 - 2020, FengMap, Ltd. All rights reserved.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { connect } from 'dva';
import router from 'umi/router';
import classnames from 'classnames';
import { Button, Icon } from 'ui';
import FmapProvider from '@/components/FmapProvider';
import { isEmpty } from 'utils/tools';
import settingConfig from 'config/setting';
import styles from './index.less';

const { minOffset, maxOffset } = settingConfig;

function Navi(props) {
    const { dispatch } = props;
    const { fmap, listGroups, focusGroupId } = props;
    const { coords, current } = props.map;

    const isReal = !!props.location.query.isReal;

    const globalRef = useRef({ navigation: null, title: '' });

    const [title, setTitle] = useState('开始导航');
    const [subhead, setSubhead] = useState('');
    const [icon, setIcon] = useState('iconzhixing');
    const [status, setStatus] = useState('start');
    const [sound, setSound] = useState(false);

    const updateTitle = useCallback(title => setTitle(title), []);

    const updateSubhead = useCallback(remain => {
        const time = remain / 70;

        const m = parseInt(time);
        const s = Math.floor((time % 1) * 60);

        let prompt = `剩余${remain.toFixed(1)}米，步行${m}分${s}秒`;

        if (remain < 1) {
            remain = 0;
            prompt = '到达终点';
        }

        setSubhead(prompt);
    }, []);

    const updateIcon = useCallback(direction => {
        let icon = 'iconzhixing';

        switch (direction) {
            case '左':
                icon = 'iconxiangzuo';
                break;
            case '右':
                icon = 'iconxiangyou';
                break;
            case '左前':
                icon = 'iconzuoqianfang';
                break;
            case '右前':
                icon = 'iconyouxianfang';
                break;
            default:
                icon = 'iconzhixing';
        }

        setIcon(icon);
    }, []);

    const handlePauseClick = useCallback(() => {
        let updateState = '';
        const navigation = globalRef.current.navigation;
        if (status === 'start') {
            navigation.pause();
            updateState = 'pause';
        } else {
            navigation.resume();
            updateState = 'start';
        }

        setStatus(updateState);
    }, [setStatus, status]);

    const handleQuitClick = useCallback(() => {
        const navigation = globalRef.current.navigation;
        navigation.stop();
        router.push('/scheme');
    }, []);

    const handleRealQuitClick = useCallback(() => {
        fmap.removeUpdateNaviLocationMarker();
        fmap.visibleLocationMarker(true);
        router.push('/scheme');
    }, [fmap]);

    const handleWalking = useCallback(
        data => {
            const { naviDescriptions, naviDescriptionsData } = globalRef.current;

            const { index, remain } = data;
            if (index === -1) return;
            globalRef.current.index = index;

            const descriptions = naviDescriptions[index];
            const descriptionsData = naviDescriptionsData[index];
            const direction = descriptionsData ? descriptionsData.endDirection : '';
            updateTitle(descriptions);
            updateSubhead(remain);
            updateIcon(direction);
        },
        [updateIcon, updateSubhead, updateTitle],
    );

    const handleRealWalking = useCallback(
        data => {
            const { naviDescriptions, naviDescriptionsData, navigation } = globalRef.current;
            const { index, remain } = data;
            if (index === -1) return;
            globalRef.current.index = index;

            fmap.updateNaviLocationMarker(data.point);

            // console.log('data.distance', data.distance);

            if (data.distance > minOffset) {
                fmap.visibleUpdateNaviLocationMarker(false);
                fmap.visibleLocationMarker(true);
            } else {
                fmap.visibleUpdateNaviLocationMarker(true);
                fmap.visibleLocationMarker(false);
            }

            if (data.distance > maxOffset) {
                navigation.clearAll();
                const { start } = coords;

                const model = { ...start, ...globalRef.current.current };

                navigation.off('walking', handleRealWalking);
                dispatch({ type: 'map/updateCoordField', payload: { field: 'start', model } });
            }

            const descriptions = naviDescriptions[index];
            const descriptionsData = naviDescriptionsData[index];
            const direction = descriptionsData.endDirection;
            updateTitle(descriptions);
            updateSubhead(remain);
            updateIcon(direction);
        },
        [fmap, updateIcon, updateSubhead, updateTitle, coords, dispatch],
    );

    const handleCrossGroup = useCallback(groupId => dispatch({ type: 'map/updateFocusGroupId', payload: { focusGroupId: groupId } }), [dispatch]);

    const handleComplete = useCallback(() => {
        router.push('/scheme');
    }, []);

    const createNaviSimulate = useCallback(
        (navigation, coords, fmap) => {
            if (isEmpty(coords)) return;

            fmap.createNaviSimulate(coords);
            fmap.visibleLocationMarker(false);
            const naviDistance = navigation.naviDistance;

            const time = naviDistance / 70;
            const m = parseInt(time);
            const s = Math.floor((time % 1) * 60);

            setSubhead(`全程约${naviDistance.toFixed(1)}米 步行预计用时 ${m}分${s}秒`);
        },
        [setSubhead],
    );

    useEffect(() => {
        const intervalTimer = setInterval(() => {
            if (fmap && fmap.loadCompleteStatus) {
                clearInterval(intervalTimer);

                const navigation = fmap.navigation;
                globalRef.current.navigation = navigation;
                createNaviSimulate(navigation, coords, fmap);
                globalRef.current.naviDescriptions = navigation.naviDescriptions;
                globalRef.current.naviDescriptionsData = navigation.naviDescriptionsData;

                //模拟导航
                if (isReal) {
                    navigation.on('walking', handleRealWalking);
                } else {
                    navigation.on('walking', handleWalking);
                    navigation.simulate();
                }

                navigation.on('complete', handleComplete);
                navigation.on('crossGroup', handleCrossGroup);

                globalRef.current.navigation = navigation;

                return () => {
                    if (isReal) {
                        navigation.off('walking', handleRealWalking);
                    } else {
                        navigation.off('walking', handleWalking);
                    }

                    navigation.off('complete', handleComplete);
                    navigation.off('crossGroup', handleCrossGroup);
                };
            }
        }, 500);

        return () => clearInterval(intervalTimer);
    }, [dispatch, coords, fmap, isReal, createNaviSimulate, handleWalking, handleComplete, handleCrossGroup, handleRealWalking]);

    useEffect(() => {
        const intervalTimer = setInterval(() => {
            if (fmap && fmap.loadCompleteStatus) {
                clearInterval(intervalTimer);

                if (isReal) {
                    const { x, y, groupID } = current;
                    fmap.navigation.locate({ x, y, groupID });
                    globalRef.current.current = { x, y, groupID };
                }
            }
        });
    }, [coords, fmap, isReal, current]);

    const handleSound = useCallback(() => {
        setSound(!sound);

        // if (globalRef.current.soundTimer) {
        //     clearInterval(globalRef.current.soundTimer);
        // }

        // globalRef.current.soundTimer = setInterval(() => {
        //     const { naviDescriptions, index } = globalRef.current;
        //     if (index === -1) return;
        //     const title = naviDescriptions[index];
        //     if (globalRef.current.title !== title) {
        //         window.tts(title, sound);
        //     }
        //     globalRef.current.title = title;
        // }, 500);
    }, [sound]);

    return (
        <FmapProvider value={{ fmap, listGroups, focusGroupId }}>
            <div className={styles.messageWrapper}>
                <div className={styles.message}>
                    <Icon className={classnames([icon, styles.directionIcon])} />
                    <div className={styles.content}>
                        <p className={styles.title}>{title}</p>
                        <p className={styles.subhead}>{subhead}</p>
                    </div>
                </div>
            </div>
            <div className={styles.controlWrapper}>
                <Button onClick={handleSound}>
                    <Icon className={sound ? 'iconlb01' : 'iconlb-gb01'} />
                </Button>
            </div>
            <div className={styles.bttnWrapper}>
                {isReal ? (
                    <Button type="primary" onClick={handleRealQuitClick}>
                        退出导航
                    </Button>
                ) : (
                    <>
                        <Button onClick={handlePauseClick}>{`${status === 'start' ? '暂停' : '开始'}`}模拟</Button>
                        <Button type="primary" onClick={handleQuitClick}>
                            退出模拟
                        </Button>
                    </>
                )}
            </div>
        </FmapProvider>
    );
}

export default connect(({ map }) => ({ map }))(Navi);
